import {Button} from '@wandb/weave/components/Button';
import {Tailwind} from '@wandb/weave/components/Tailwind';
import React, {useCallback, useEffect, useRef, useState} from 'react';

import {useWFHooks} from '../wfReactInterface/context';
import {CallSchema} from '../wfReactInterface/wfDataModelHooksInterface';
import {opNiceName} from './opNiceName';

export const EditableCallName: React.FC<{
  call: CallSchema;
}> = ({call}) => {
  const defaultDisplayName = opNiceName(call.spanName);
  const displayNameIsEmpty =
    call.displayName == null || call.displayName === '';
  // The name to display is the displayName if it is not empty, otherwise
  // it is the defaultDisplayName.
  const nameToDisplay = displayNameIsEmpty
    ? defaultDisplayName
    : call.displayName!;

  const {useCallUpdateFunc: useCallRenameFunc} = useWFHooks();
  const callRename = useCallRenameFunc();
  const [currNameToDisplay, setCurrNameToDisplay] = useState(nameToDisplay);
  const [isEditing, setIsEditing] = useState(false);

  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setCurrNameToDisplay(nameToDisplay);
  }, [nameToDisplay]);

  const saveName = useCallback(
    (newName: string) => {
      // In the case that `newName` is empty or equal to the default name,
      // then we want to set the name to empty string.
      if (newName === '' || newName === defaultDisplayName) {
        setCurrNameToDisplay(defaultDisplayName);
        if (!displayNameIsEmpty) {
          callRename({
            entity: call.entity,
            project: call.project,
            callID: call.callId,
            newName: '',
          });
        }
      } else if (newName !== call.displayName) {
        // Else, update the name only if it is different from the current name.
        setCurrNameToDisplay(newName);
        callRename({
          entity: call.entity,
          project: call.project,
          callID: call.callId,
          newName,
        });
      }
    },
    [defaultDisplayName, call, displayNameIsEmpty, callRename]
  );

  const onBlur = () => {
    setIsEditing(false);
    saveName(currNameToDisplay);
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Escape') {
      setCurrNameToDisplay(nameToDisplay);
      inputRef.current?.blur();
    } else if (e.key === 'Enter') {
      saveName(currNameToDisplay);
      inputRef.current?.blur();
    }
  };

  return (
    <Tailwind style={{width: '100%'}}>
      <div className="group relative w-full">
        <input
          ref={inputRef}
          className={`min-w-[100px] w-full rounded px-8 py-4 ${!isEditing ? 'pr-[62px]' : ''} bg-transparent font-semibold border-2 border-transparent cursor-pointer transition-colors overflow-hidden text-ellipsis hover:bg-moon-100 focus:cursor-text focus:bg-white focus:border-teal-350 focus:px-6 focus:py-4 focus:outline-none`}
          value={currNameToDisplay}
          placeholder={defaultDisplayName}
          onChange={e => setCurrNameToDisplay(e.target.value)}
          onKeyDown={onKeyDown}
          onFocus={() => setIsEditing(true)}
          onBlur={onBlur}
        />
        {!isEditing && (
          <div
            className="absolute top-6 opacity-0 transition-opacity group-hover:opacity-100"
            style={{left: `${currNameToDisplay.length + 1}ch`}}>
            <Button
              size="small"
              variant="ghost"
              icon="pencil-edit"
              onClick={() => {
                inputRef.current?.focus();
              }}>
              Edit
            </Button>
          </div>
        )}
      </div>
    </Tailwind>
  );
};
